import { useId } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { googleCalendarStatusSchema } from "@gioia365/contracts";
import { apiRequest } from "../api/http";
import { appointmentKeys, googleCalendarStatusQuery } from "../appointments/appointments-queries";
import { Button } from "../design/components/Button";
import { Reason } from "../design/components/Reason";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";

// Google Calendar in «Il tuo account» (piano M6, R7, Task 6; SEZ-11): ognuno collega il suo calendario, e le disco e le demo che
// prenota nascono lì con il link Meet. Senza le due variabili Google sul server la sezione è spenta e dice perché; «Collega» porta
// a Google con una navigazione vera (la richiesta firmata la prepara il server), «Scollega» toglie subito l'uso del collegamento.
// Nessun gettone arriva mai al browser: il server dice soltanto lo stato e l'indirizzo collegato.

export function GoogleCalendarCard() {
  const t = useT();
  const toast = useToast();
  const titleId = useId();
  const queryClient = useQueryClient();
  const status = useQuery(googleCalendarStatusQuery());
  const disconnect = useMutation({
    mutationFn: () => apiRequest("POST", "/api/google/disconnect", { schema: googleCalendarStatusSchema }),
    onSuccess: (next) => {
      queryClient.setQueryData(appointmentKeys.googleStatus, next);
      void queryClient.invalidateQueries({ queryKey: ["scheda"], predicate: (query) => query.queryKey.includes("appointments") });
      toast.show(t("appointments.account.disconnected"), "success");
    },
    onError: (error) => toast.show(messageForError(t, error), "warning"),
  });
  const data = status.data;
  let line: string | null = null;
  if (data && !data.available) line = t("appointments.account.unavailable");
  else if (data?.state === "connected") line = t("appointments.account.connected", { email: data.email ?? "—" });
  else if (data?.state === "revoked") line = t("appointments.account.revoked");
  else if (data?.state === "failed") line = t("appointments.account.failed");
  else if (data) line = t("appointments.account.notConnected");

  return (
    <section className="g-account__section" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("appointments.account.title")}</h3>
      <p className="g-auth__muted">{t("appointments.account.help")}</p>
      {status.isError ? <Reason tone="warning">{messageForError(t, status.error)}</Reason> : null}
      {line ? <Reason tone={data && !data.available ? "warning" : undefined}>{line}</Reason> : null}
      {data?.available && data.state === "connected" ? (
        <div>
          <Button disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
            {t("appointments.account.disconnect")}
          </Button>
        </div>
      ) : null}
      {data?.available && data.state !== "connected" ? (
        <div>
          {/* Una navigazione vera: il server risponde con il rinvio a Google, che torna su `/api/google/oauth/callback`. */}
          <a className="g-button" data-variant="primary" href="/api/google/oauth/start">
            {t("appointments.account.connect")}
          </a>
        </div>
      ) : null}
    </section>
  );
}
